import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Package, Calendar, CreditCard, ChevronRight, AlertCircle, Download, ExternalLink } from 'lucide-react';
import { getMyOrders, downloadFile } from '../services/paymentService';
import { API_BASE_URL } from '../services/api';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';

const MyOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingOrder, setDownloadingOrder] = useState(null);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const res = await getMyOrders();
                if (res.success) {
                    setOrders(res.data);
                }
            } catch (error) {
                console.error('Error fetching orders:', error);
                toast.error('Failed to load your orders');
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    const handleDownload = async (orderId, productTitle) => {
        try {
            setDownloadingOrder(orderId);
            const fileName = `${productTitle.replace(/\s+/g, '_')}_MehareTech.zip`;
            await downloadFile(orderId, fileName);
            toast.success('Download started!');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download file');
        } finally {
            setDownloadingOrder(null);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-500/10 text-green-500 border-green-500/20';
            case 'pending': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
            case 'failed': return 'bg-red-500/10 text-red-500 border-red-500/20';
            default: return 'bg-slate-500/10 text-slate-500 border-slate-500/20';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <SEO title="My Orders | MehareTech" description="View your purchase history and order details." />

            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-black text-slate-900 dark:text-white">Order History</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Track your purchases and downloads.</p>
                </div>
                <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/10 text-primary">
                    <ShoppingBag size={24} />
                </div>
            </div>

            {orders.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white dark:bg-slate-900/50 p-12 rounded-3xl border border-dashed border-slate-200 dark:border-white/10 text-center space-y-4"
                >
                    <div className="w-20 h-20 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto text-slate-400">
                        <Package size={40} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold dark:text-white">No orders found</h3>
                        <p className="text-slate-500 dark:text-slate-400 text-sm max-w-sm mx-auto mt-2">
                            You haven't made any purchases yet. Your paid projects and digital assets will appear here.
                        </p>
                    </div>
                    <button className="px-6 py-2.5 bg-primary text-white font-bold rounded-xl hover:scale-105 transition-all text-sm">
                        Browse Products
                    </button>
                </motion.div>
            ) : (
                <div className="grid grid-cols-1 gap-6">
                    {orders.map((order, index) => (
                        <motion.div
                            key={order._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-white dark:bg-slate-900/50 rounded-xl md:rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden hover:shadow-lg transition-all group"
                        >
                            <div className="p-4 md:p-6">
                                <div className="flex flex-col lg:flex-row gap-6">
                                    {/* Product Image */}
                                    <div className="w-full lg:w-40 aspect-video lg:aspect-square rounded-lg md:rounded-xl overflow-hidden bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 shrink-0">
                                        <img
                                            src={order.product?.thumbnail_image ? `${API_BASE_URL}${order.product.thumbnail_image}` : 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=400'}
                                            alt={order.product?.product_title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                    </div>

                                    {/* Order Details */}
                                    <div className="flex-1 space-y-4">
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div>
                                                <h3 className="text-lg md:text-xl font-black text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                                                    {order.product?.product_title || 'Digital Product'}
                                                </h3>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                                                    ID: {order.razorpay_order_id}
                                                </p>
                                            </div>
                                            <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(order.status)}`}>
                                                {order.status}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 pt-4 border-t border-slate-100 dark:border-white/5">
                                            <div className="space-y-0.5">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                                    <Calendar size={10} /> Date
                                                </p>
                                                <p className="text-xs font-bold dark:text-white">
                                                    {new Date(order.createdAt).toLocaleDateString('en-IN', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </p>
                                            </div>
                                            <div className="space-y-0.5">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                                    <CreditCard size={10} /> Price
                                                </p>
                                                <p className="text-xs font-bold dark:text-white">
                                                    ₹{order.amount}
                                                </p>
                                            </div>
                                            <div className="space-y-0.5">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                                    <AlertCircle size={10} /> Payment
                                                </p>
                                                <p className="text-[10px] font-bold dark:text-white truncate max-w-[100px]">
                                                    {order.razorpay_payment_id || '---'}
                                                </p>
                                            </div>
                                            <div className="space-y-0.5">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                                    <Package size={10} /> Asset
                                                </p>
                                                <p className="text-xs font-bold dark:text-white uppercase">
                                                    Digital
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-3 pt-4">
                                            {order.status === 'completed' ? (
                                                <>
                                                    <button
                                                        onClick={() => handleDownload(order._id, order.product?.product_title)}
                                                        disabled={downloadingOrder === order._id}
                                                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-xs font-black uppercase tracking-widest rounded-lg hover:scale-105 transition-all disabled:opacity-50"
                                                    >
                                                        {downloadingOrder === order._id ? (
                                                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                        ) : (
                                                            <Download size={14} />
                                                        )}
                                                        {downloadingOrder === order._id ? 'Preparing...' : 'Download Files'}
                                                    </button>
                                                    <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-white/5 text-slate-900 dark:text-white border border-slate-200 dark:border-white/10 text-xs font-black uppercase tracking-widest rounded-lg hover:bg-slate-50 dark:hover:bg-white/10 transition-all">
                                                        <ExternalLink size={14} /> View Details
                                                    </button>
                                                </>
                                            ) : order.status === 'pending' ? (
                                                <button className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white text-xs font-black uppercase tracking-widest rounded-lg hover:scale-105 transition-all">
                                                    Complete Payment
                                                </button>
                                            ) : (
                                                <p className="text-xs font-bold text-red-500 uppercase tracking-widest py-2">
                                                    Payment failed. Please try again or contact support.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyOrders;
